import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { calcIronScore } from './xpUtils';
import { SocialErrorCard, ReactionRow, applyReactionToggle, CommentThread, CommentIcon } from './shared';
import { SectionTitle } from '../../components/kit';

export function SocialFeed({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  const emailRef=useRef(email);
  useEffect(()=>{emailRef.current=email;},[email]);
  const [feedData,setFeedData]=useState(null);
  const [notifs,setNotifs]=useState(null);
  const [loadErr,setLoadErr]=useState(false);
  const [newPostCount,setNewPostCount]=useState(0);
  const [openThread,setOpenThread]=useState(null); // event id whose comments are expanded
  const lastSeenIdRef=useRef(null);
  const loadFeed=(silent)=>{const em=emailRef.current;if(!silent)setLoadErr(false);if(em){
    SocialAPI.getFeed(em).then(r=>{
      if(r){
        // Initialize lastSeenIdRef on first load so new-post detection works immediately
        if(!lastSeenIdRef.current&&r.events?.length>0)lastSeenIdRef.current=r.events[0].id;
        // ── T3 #11: detect new posts since last seen ──
        if(lastSeenIdRef.current&&r.events){
          // Events are newest-first. Count non-own events above the last-seen marker.
          // If the marker scrolled out of the returned window, findIndex returns -1 — treat
          // every non-own event as new (previously `idx < -1` was always false → pill never showed).
          const seenIdx=r.events.findIndex(ev2=>ev2.id===lastSeenIdRef.current);
          const cutoff=seenIdx===-1?r.events.length:seenIdx;
          const newOnes=r.events.slice(0,cutoff).filter(ev=>!ev.isOwn&&ev.id!==lastSeenIdRef.current);
          setNewPostCount(newOnes.length); // absolute, not cumulative — each poll recounts the same events
        }
        setFeedData(r);
        // Cache latest feed for Home friend nudge
        LS.set("ft-feed-cache",{events:(r.events||[]).slice(0,20),ts:Date.now()});
      } else if(!silent){setLoadErr(true);}
    });
    SocialAPI.getNotifications(em).then(setNotifs);
  }};
  useEffect(()=>{loadFeed();},[email]);
  // ── Auto-refresh every 60s when feed is visible. Pause when tab is hidden
  // and re-include `email` so we re-poll the correct account after sign-out/in.
  useEffect(()=>{
    if(!email)return;
    let id=null;
    const start=()=>{ if(id)return; id=setInterval(()=>loadFeed(true),60000); };
    const stop=()=>{ if(id){clearInterval(id);id=null;} };
    const visHandler=()=>{ document.visibilityState==="visible"?start():stop(); };
    visHandler();
    document.addEventListener("visibilitychange",visHandler);
    return()=>{ stop(); document.removeEventListener("visibilitychange",visHandler); };
  },[email]);
  const reactToEvent=async(eventId,key)=>{
    // Optimistic toggle so the button responds instantly; server is the source of truth on next load.
    setFeedData(fd=>fd?{...fd,events:(fd.events||[]).map(ev=>ev.id===eventId?applyReactionToggle(ev,key):ev)}:fd);
    LS.set("ft-reacted-today",today()); // marks m_react mission as completable
    const r=await SocialAPI.react(email,eventId,key);
    if(!r)SocialAPI.getFeed(email).then(fd=>{if(fd)setFeedData(fd);});
  };

  const streak=useStreak(s.workouts);
  const weekW=(s.workouts||[]).filter(w=>w.date>=ago(7)).length;
  const protDays=(s.nutrition||[]).filter(n=>n.date>=ago(7)&&(n.protein||0)>=(s.goals?.protein||180)).length;
  const readiness=calcReadiness(s);
  const {xp,rank}=useMemo(()=>calcIronScore(s),[s.workouts,s.nutrition,s.photos,s.checkins,s.body,s._xpTs]);
  const todayN=(s.nutrition||[]).filter(n=>n.date===today());
  const todayCal=todayN.reduce((a,n)=>a+(n.cal||0),0);
  const todayProt=todayN.reduce((a,n)=>a+(n.protein||0),0);
  const nextWorkout=(()=>{const sched=s.schedule?.weekly||{};const dow=new Date().getDay();for(let i=0;i<7;i++){const d2=(dow+i)%7;const label=s.schedule?.overrides?.[ago(-i)]||sched[d2];if(label&&label!=="Rest"&&label!=="Off")return{day:i===0?"Today":i===1?"Tomorrow":["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][(dow+i)%7],label};}return null;})();

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Activity Feed</div>
      {loadErr&&<SocialErrorCard label="Could not load feed" onRetry={()=>loadFeed()}/>}
      {/* Today card */}
      <Card style={{padding:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <SectionTitle icon={Icons.calendar}>Today</SectionTitle>
          <DailyMissionsCard s={s} d={d} compact/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
          <div style={{padding:"8px 10px",borderRadius:8,background:V.card}}>
            <div style={{fontSize:11,color:V.text3}}>Readiness</div>
            <div style={{fontSize:16,fontWeight:800,color:readiness.color,fontFamily:V.mono}}>{readiness.score}</div>
          </div>
          <div style={{padding:"8px 10px",borderRadius:8,background:V.card}}>
            <div style={{fontSize:11,color:V.text3}}>Protein</div>
            <div style={{fontSize:16,fontWeight:800,color:todayProt>=(s.goals?.protein||180)?V.accent:V.warn,fontFamily:V.mono}}>{todayProt}g</div>
          </div>
          <div style={{padding:"8px 10px",borderRadius:8,background:V.card}}>
            <div style={{fontSize:11,color:V.text3}}>Calories</div>
            <div style={{fontSize:16,fontWeight:800,color:V.text,fontFamily:V.mono}}>{todayCal}</div>
          </div>
          <div style={{padding:"8px 10px",borderRadius:8,background:V.card}}>
            <div style={{fontSize:11,color:V.text3}}>Next</div>
            <div style={{fontSize:12,fontWeight:700,color:V.accent}}>{nextWorkout?`${nextWorkout.day}: ${nextWorkout.label}`:"Rest"}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {[{l:"Workout",t:"log_workout"},{l:"Meal",t:"log_nutrition"},{l:"Check-in",t:"home"},{l:"Photo",t:"track_photos"}].map(a=>(
            <button key={a.t} onClick={()=>d({type:"TAB",tab:a.t})} style={{flex:1,padding:"8px 4px",borderRadius:8,
              background:`${V.accent}08`,border:`1px solid ${V.accent}20`,cursor:"pointer",
              fontSize:11,color:V.accent,fontWeight:700,fontFamily:V.font,WebkitTapHighlightColor:"transparent"}}>{a.l}</button>
          ))}
        </div>
      </Card>
      {/* Highlights */}
      <Card style={{padding:14}}>
        <SectionTitle icon={Icons.zap} color={V.warn} style={{marginBottom:8}}>Highlights</SectionTitle>
        {[{t:`${weekW} workouts this week`,e:weekW>=5?"🔥":weekW>=3?"💪":"📈",show:true},
          {t:`Protein hit ${protDays} of 7 days`,e:protDays>=7?"⭐":protDays>=5?"✅":"🎯",show:true},
          {t:`${streak}-day streak`,e:streak>=30?"💎":streak>=7?"🔥":"🔄",show:streak>0}].filter(h=>h.show).map((h,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0"}}>
            <span style={{color:V.text2}}>{h.t}</span><span>{h.e}</span>
          </div>
        ))}
      </Card>
      {/* Accountability */}
      {!(s.workouts||[]).some(w=>w.date===today())&&!(s.workouts||[]).some(w=>w.date===ago(1))&&(
        <Card style={{padding:14,border:`1px solid ${V.warn}20`}}>
          <SectionTitle icon={Icons.flame} color={V.warn} size={12}>Get after it</SectionTitle>
          <div style={{fontSize:11,color:V.text3,marginTop:4}}>2 days without a workout. Even 1 set counts.</div>
          <Btn full onClick={()=>d({type:"TAB",tab:"log_workout"})} s={{marginTop:8,background:V.warn}}>Log 1 Set →</Btn>
        </Card>
      )}
      {/* Competitive status */}
      {(()=>{
        const rivals=LS.get("ft-rivals")||[];
        const duels=LS.get("ft-duels")||[];
        const activeWar=(()=>{
          const dow=new Date().getDay();const mondayOffset=dow===0?6:dow-1;
          const weekStart=ago(mondayOffset);
          const weekW2=(s.workouts||[]).filter(w=>w.date>=weekStart).length;
          return weekW2;
        })();
        if(rivals.length===0&&duels.length===0)return null;
        return(
          <Card style={{padding:14,background:`linear-gradient(135deg,${V.danger}06,${V.accent}04)`,
            border:`1px solid ${V.danger}20`}}>
            <SectionTitle icon={Icons.trophy} color={V.danger} style={{marginBottom:8}}>Competition status</SectionTitle>
            {duels.length>0&&(
              <div style={{marginBottom:8}}>
                <div style={{fontSize:11,color:V.text3,marginBottom:4}}>ACTIVE DUELS</div>
                {duels.slice(0,2).map((duel,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    padding:"4px 0",borderBottom:`1px solid ${V.divider}`}}>
                    <span style={{fontSize:11,color:V.text2}}>{duel.friendName}</span>
                    <span style={{fontSize:11,fontWeight:700,padding:"2px 6px",borderRadius:4,
                      background:V.inputBg,color:V.text3}}>
                      {duel.metric} · {Math.max(0,Math.round((new Date(duel.endDate)-new Date())/86400000))}d left
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={()=>d({type:"TAB",tab:"social_duels"})} style={{
              width:"100%",padding:"7px",borderRadius:8,background:"transparent",
              border:`1px solid ${V.cardBorder}`,cursor:"pointer",
              fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>
              View All Competition →
            </button>
          </Card>
        );
      })()}
      
      {/* ── T3 #11: New-post pill + Friend Activity card ── */}
      {newPostCount>0&&(
        <button onClick={()=>{setNewPostCount(0);lastSeenIdRef.current=feedData?.events?.[0]?.id||null;}}
          style={{width:"100%",padding:"8px",borderRadius:10,
            background:`${V.accent}10`,border:`1px solid ${V.accent}30`,cursor:"pointer",
            fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font,
            display:"flex",alignItems:"center",justifyContent:"center",gap:6,
            animation:"newPostPulse 1.5s ease-in-out infinite"}}>
          <span style={{fontSize:14}}>✨</span>
          {newPostCount} new post{newPostCount>1?"s":""} — tap to see
        </button>
      )}
      <style>{`@keyframes newPostPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,245,160,0.3)}50%{box-shadow:0 0 0 6px rgba(0,245,160,0)}}`}</style>
      {feedData?.events?.filter(e=>!e.isOwn).length>0&&(
        <Card style={{padding:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <SectionTitle icon={Icons.activity} color={V.accent2}>Friend activity</SectionTitle>
            <button onClick={()=>loadFeed(false)} aria-label="Refresh feed"
              style={{background:"none",border:`1px solid ${V.cardBorder}`,borderRadius:8,cursor:"pointer",display:"flex",alignItems:"center",gap:4,
                fontSize:11,color:V.text3,padding:"3px 8px",fontFamily:V.font}}>{Icons.refresh({size:12,color:V.text3})}Refresh</button>
          </div>
          {feedData.events.filter(e=>!e.isOwn).slice(0,8).map(ev=>(
            <div key={ev.id} style={{padding:"8px 0",borderBottom:`1px solid ${V.divider}`}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div><span style={{fontSize:12,fontWeight:600,color:V.text}}>{ev.name}</span>
                  <span style={{fontSize:11,color:V.text3}}>{" "}{ev.type==="WorkoutLogged"?"logged a workout":ev.type==="PRHit"?"hit a PR 🎉":ev.type==="QuickMessage"?`sent: ${ev.data?.msg||"a message"}`:ev.type==="AccountabilitySet"?"set an accountability partner":ev.type==="GroupChat"?"sent a group message":ev.type==="ChallengeAchieved"?`reached ${ev.data?.tier||"a new tier"} 🏆`:"was active"}</span></div>
                <span style={{fontSize:11,color:V.text3}}>{fmtShort(ev.created_at?.split("T")[0])}</span>
              </div>
              {/* Event detail */}
              {ev.type==="WorkoutLogged"&&ev.data?.exercises&&(
                <div style={{fontSize:11,color:V.text3,marginTop:2}}>{Array.isArray(ev.data.exercises)?ev.data.exercises.slice(0,3).join(", "):""}{ev.data.sets?` · ${ev.data.sets} sets`:""}</div>
              )}
              {ev.type==="PRHit"&&ev.data&&(
                <div style={{fontSize:11,color:V.warn,marginTop:2,fontFamily:V.mono}}>{ev.data.exercise}: {ev.data.weight}×{ev.data.reps} (E1RM: {ev.data.e1rm})</div>
              )}
              {/* Reactions */}
              <div style={{display:"flex",alignItems:"flex-end",gap:6,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:0}}><ReactionRow ev={ev} onReact={reactToEvent}/></div>
                <button type="button" onClick={()=>setOpenThread(openThread===ev.id?null:ev.id)} aria-expanded={openThread===ev.id}
                  style={{display:"flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:8,marginTop:6,cursor:"pointer",fontFamily:V.font,
                    background:openThread===ev.id?`${V.accent}18`:"transparent",border:`1px solid ${openThread===ev.id?V.accent+"40":V.cardBorder}`,
                    fontSize:11,fontWeight:700,color:openThread===ev.id?V.accent:V.text3}}>
                  <CommentIcon size={13} color={openThread===ev.id?V.accent:V.text3}/>{ev.comments||0}
                </button>
              </div>
              {openThread===ev.id&&<CommentThread email={email} eventId={ev.id}
                onCount={n=>setFeedData(fd=>fd?{...fd,events:(fd.events||[]).map(e=>e.id===ev.id?{...e,comments:n}:e)}:fd)}/>}
            </div>
          ))}
        </Card>
      )}
      {/* Notifications */}
      {notifs?.unread>0&&(
        <Card style={{padding:14,border:`1px solid ${V.accent}20`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:12,fontWeight:700,color:V.accent}}>🔔 {notifs.unread} notification{notifs.unread>1?"s":""}</span>
            <button onClick={()=>{SocialAPI.markRead(email,"all");setNotifs({...notifs,unread:0});}}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:V.text3}}>Clear</button>
          </div>
          {notifs.notifications.filter(n=>!n.read).slice(0,3).map(n=>(
            <div key={n.id} style={{padding:"4px 0",fontSize:11,color:V.text2}}><b>{n.title}</b> — {n.body}</div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── Social Sub-Page: Friends ───
